// src/wallet/walletClient.js
import { ethers } from "ethers";
import { appKit } from "./web3modal";

let currentProvider = null;
let currentSigner = null;
let currentAddress = null;
let currentWalletType = null; // "injected" | "walletconnect"

const ACTIVE_WALLET_KEY = "chainforge_connected_wallet";
const KNOWN_WALLETS_KEY = "chainforge_known_wallets";
const LOGOUT_SUPPRESS_KEY = "chainforge_wallet_restore_suppressed";

const WALLET_PENDING_EVENT = "chainforge:wallet-request-state";

let activeWalletRequestLabel = "";

function emitWalletRequestState() {
  try {
    window.dispatchEvent(
      new CustomEvent(WALLET_PENDING_EVENT, {
        detail: getWalletRequestState(),
      }),
    );
  } catch {
    // ignore
  }
}

export function getWalletRequestState() {
  return {
    pending: Boolean(activeWalletRequestLabel),
    label: activeWalletRequestLabel || "",
  };
}

export function subscribeToWalletRequestState(handler) {
  if (typeof window === "undefined") return () => { };

  const listener = (event) => {
    handler?.(event.detail || getWalletRequestState());
  };

  window.addEventListener(WALLET_PENDING_EVENT, listener);

  return () => {
    window.removeEventListener(WALLET_PENDING_EVENT, listener);
  };
}

export function setWalletRequestLabel(label) {
  activeWalletRequestLabel = String(label || "").trim();
  emitWalletRequestState();
}

export function clearWalletRequestLabel() {
  activeWalletRequestLabel = "";
  emitWalletRequestState();
}

export function resetWalletRequestLock() {
  activeWalletRequestLabel = "";
  emitWalletRequestState();
}

export function isWalletRequestPendingError(err) {
  const code = err?.code || err?.info?.error?.code;
  const message = String(
    err?.message ||
    err?.shortMessage ||
    err?.reason ||
    err?.info?.error?.message ||
    "",
  ).toLowerCase();

  return (
    code === -32002 ||
    message.includes("request already pending") ||
    message.includes("already pending") ||
    message.includes("previous request") ||
    message.includes("pending request")
  );
}

export function humanizeWalletRequestError(
  err,
  fallback = "Wallet request failed.",
) {
  if (isWalletRequestPendingError(err)) {
    return "A wallet request is already open. Open your wallet app or browser extension, finish or cancel the request, then try again.";
  }

  const code = err?.code || err?.info?.error?.code;
  const message = String(
    err?.message ||
    err?.shortMessage ||
    err?.reason ||
    err?.info?.error?.message ||
    "",
  );

  const lower = message.toLowerCase();

  if (
    code === 4001 ||
    code === "ACTION_REJECTED" ||
    lower.includes("user rejected") ||
    lower.includes("user denied") ||
    lower.includes("rejected the request")
  ) {
    return "Wallet request was cancelled.";
  }

  if (lower.includes("timed out") || lower.includes("timeout")) {
    return "Wallet request timed out. Open your wallet app or browser extension, finish the pending request, then try again.";
  }

  return message || fallback;
}

/**
 * Save active wallet in memory
 */

function normalizeAddress(address) {
  return String(address || "").trim().toLowerCase();
}
function utf8ToHex(value) {
  return ethers.hexlify(ethers.toUtf8Bytes(String(value || "")));
}

function withTimeout(promise, ms, message) {
  let timer;

  const timeoutPromise = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error(message)), ms);
  });

  return Promise.race([
    promise.finally(() => clearTimeout(timer)),
    timeoutPromise,
  ]);
}

export function getKnownWallets() {
  try {
    const raw = localStorage.getItem(KNOWN_WALLETS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function rememberKnownWallet(wallet) {
  const address = String(wallet?.address || "").trim();
  if (!address) return;

  try {
    const normalized = normalizeAddress(address);
    const current = getKnownWallets();

    const next = [
      {
        address,
        walletType: wallet?.walletType || null,
        label: wallet?.label || null,
        lastUsedAt: Date.now(),
      },
      ...current.filter(
        (item) => normalizeAddress(item?.address) !== normalized,
      ),
    ].slice(0, 10);

    localStorage.setItem(KNOWN_WALLETS_KEY, JSON.stringify(next));
  } catch {
    // ignore storage errors
  }
}

export function removeKnownWallet(address) {
  try {
    const normalized = normalizeAddress(address);
    const current = getKnownWallets();
    const next = current.filter(
      (item) => normalizeAddress(item?.address) !== normalized,
    );
    localStorage.setItem(KNOWN_WALLETS_KEY, JSON.stringify(next));
  } catch {
    // ignore
  }
}

export function setWalletRestoreSuppressed(value) {
  try {
    if (value) {
      localStorage.setItem(LOGOUT_SUPPRESS_KEY, "1");
    } else {
      localStorage.removeItem(LOGOUT_SUPPRESS_KEY);
    }
  } catch {
    // ignore
  }
}

export function isWalletRestoreSuppressed() {
  try {
    return localStorage.getItem(LOGOUT_SUPPRESS_KEY) === "1";
  } catch {
    return false;
  }
}

function clearWalletConnectorStorageForExplicitDisconnect() {
  if (typeof window === "undefined") return;

  const exactKeys = new Set([
    ACTIVE_WALLET_KEY,
    KNOWN_WALLETS_KEY,
    LOGOUT_SUPPRESS_KEY,
    "chainforge_wallet_address",
  ]);

  const walletConnectorHints = [
    "@appkit",
    "appkit",
    "walletconnect",
    "wc@",
    "reown",
    "web3modal",
    "wagmi",
  ];

  const cleanStorage = (storage) => {
    try {
      Object.keys(storage).forEach((key) => {
        const lower = String(key || "").toLowerCase();

        if (
          exactKeys.has(key) ||
          walletConnectorHints.some((hint) => lower.includes(hint))
        ) {
          storage.removeItem(key);
        }
      });
    } catch {
      // ignore browser storage cleanup errors
    }
  };

  cleanStorage(window.localStorage);
  cleanStorage(window.sessionStorage);

  setWalletRestoreSuppressed(true);
}

function emitWalletChanged() {
  try {
    window.dispatchEvent(new CustomEvent("chainforge:wallet-changed"));
  } catch {
    // ignore
  }
}

function setCurrentWallet({ provider, signer, address, walletType }) {
  currentProvider = provider || null;
  currentSigner = signer || null;
  currentAddress = String(address || "").trim() || null;
  currentWalletType = walletType || null;

  try {
    if (currentAddress) {
      localStorage.setItem(
        ACTIVE_WALLET_KEY,
        JSON.stringify({
          address: currentAddress,
          walletType: currentWalletType,
        }),
      );

      rememberKnownWallet({
        address: currentAddress,
        walletType: currentWalletType,
      });
    } else {
      localStorage.removeItem(ACTIVE_WALLET_KEY);
    }
  } catch {
    // ignore storage errors
  }
}

/**
 * Connect injected wallet, such as browser extension wallets
 */
export async function connectInjected() {
  if (!window.ethereum) {
    throw new Error("No injected wallet found.");
  }

  setWalletRestoreSuppressed(false);

  const provider = new ethers.BrowserProvider(window.ethereum);
  const accounts = await provider.send("eth_requestAccounts", []);
  const address = accounts?.[0];

  if (!address) {
    throw new Error("No wallet address returned.");
  }

  const signer = await provider.getSigner();

  setCurrentWallet({
    provider,
    signer,
    address,
    walletType: "injected",
  });

  return { provider, signer, address };
}

/**
 * Connect using WalletConnect / Reown AppKit
 */
function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function readAddressFromRawProvider(provider) {
  if (!provider?.request) return "";

  try {
    const accounts = await provider.request({ method: "eth_accounts" });
    const address =
      Array.isArray(accounts) && accounts[0] ? String(accounts[0]).trim() : "";
    return address;
  } catch {
    return "";
  }
}

async function readAddressFromBrowserProvider(browserProvider) {
  try {
    const signer = await browserProvider.getSigner();
    const signerAddress = String(await signer.getAddress()).trim();
    return signerAddress;
  } catch {
    return "";
  }
}

async function readBestAvailableWalletAddress() {
  try {
    const appKitAddress = String(appKit.getAddress?.() || "").trim();
    if (appKitAddress) return appKitAddress;
  } catch {
    // ignore
  }

  try {
    const rawProvider = appKit.getWalletProvider?.();
    if (rawProvider) {
      const rawAddress = await readAddressFromRawProvider(rawProvider);
      if (rawAddress) return rawAddress;

      const browserProvider = new ethers.BrowserProvider(rawProvider);
      const signerAddress = await readAddressFromBrowserProvider(browserProvider);
      if (signerAddress) return signerAddress;
    }
  } catch {
    // ignore
  }

  return "";
}

async function waitForAppKitDisconnect(timeoutMs = 8000) {
  // Fast path
  try {
    if (!Boolean(appKit.getIsConnected?.())) {
      return true;
    }
  } catch {
    return true;
  }

  return new Promise((resolve) => {
    let done = false;
    let unsubscribe = null;

    const finish = () => {
      if (done) return;
      done = true;
      clearTimeout(timer);

      try {
        unsubscribe?.();
      } catch {
        // ignore
      }

      resolve(true);
    };

    const timer = setTimeout(() => {
      finish();
    }, timeoutMs);

    try {
      unsubscribe = appKit?.subscribeProvider?.((state) => {
        const isConnected =
          typeof state?.isConnected === "boolean"
            ? state.isConnected
            : Boolean(appKit.getIsConnected?.());

        if (!isConnected) {
          finish();
        }
      });
    } catch {
      finish();
    }
  });
}

async function waitForAppKitAddress(timeoutMs = 30000, intervalMs = 250) {
  const started = Date.now();

  while (Date.now() - started < timeoutMs) {
    try {
      const isConnected = Boolean(appKit.getIsConnected?.());
      const address = await readBestAvailableWalletAddress();

      if (isConnected && address) {
        return address;
      }

      // Some connectors expose the address before AppKit flips connected=true.
      // Accept it once the provider exists and address is real.
      const provider = appKit.getWalletProvider?.();
      if (provider && address) {
        return address;
      }
    } catch {
      // ignore
    }

    await sleep(intervalMs);
  }

  throw new Error("Wallet connection was cancelled or not completed.");
}

async function waitForDifferentAppKitAddress(previousAddress = "", timeoutMs = 30000, intervalMs = 250) {
  const started = Date.now();
  const previous = normalizeAddress(previousAddress);

  while (Date.now() - started < timeoutMs) {
    try {
      const address = await readBestAvailableWalletAddress();
      const normalized = normalizeAddress(address);

      if (address && normalized !== previous) {
        return address;
      }
    } catch {
      // ignore
    }

    await sleep(intervalMs);
  }

  throw new Error("Wallet switch was cancelled or not completed.");
}

async function waitForWalletProvider(timeoutMs = 8000, intervalMs = 250) {
  return new Promise((resolve, reject) => {
    let done = false;
    let unsubscribeState = null;
    let pollTimer = null;

    const finish = (fn, value) => {
      if (done) return;
      done = true;

      if (pollTimer) {
        clearInterval(pollTimer);
      }

      try {
        unsubscribeState?.();
      } catch {
        // ignore cleanup errors
      }

      fn(value);
    };

    const failCancelled = () => {
      finish(reject, new Error("Wallet connection was cancelled."));
    };

    // Fast path
    try {
      const immediateProvider = appKit.getWalletProvider?.();
      if (immediateProvider) {
        finish(resolve, immediateProvider);
        return;
      }
    } catch {
      // ignore
    }

    // Watch modal open/close state
    try {
      const state = appKit.getState?.();
      if (state?.open === false && !appKit.getWalletProvider?.()) {
        failCancelled();
        return;
      }
    } catch {
      // ignore
    }

    try {
      unsubscribeState = appKit.subscribeState?.((state) => {
        const provider = appKit.getWalletProvider?.();

        if (provider) {
          finish(resolve, provider);
          return;
        }

        if (state?.open === false) {
          failCancelled();
        }
      });
    } catch {
      // ignore if subscribeState is unavailable
    }

    // Poll provider as fallback
    pollTimer = setInterval(() => {
      try {
        const provider = appKit.getWalletProvider?.();
        if (provider) {
          finish(resolve, provider);
        }
      } catch {
        // ignore
      }
    }, intervalMs);

    // Hard timeout fallback
    setTimeout(() => {
      failCancelled();
    }, timeoutMs);
  });
}

async function waitForMatchingSigner(
  browserProvider,
  expectedAddress,
  timeoutMs = 15000,
  intervalMs = 250,
) {
  const started = Date.now();
  const expected = normalizeAddress(expectedAddress);
  let lastSeenSignerAddress = "";

  while (Date.now() - started < timeoutMs) {
    try {
      const nextSigner = await browserProvider.getSigner();
      const signerAddress = String(await nextSigner.getAddress()).trim();
      lastSeenSignerAddress = signerAddress;

      if (normalizeAddress(signerAddress) === expected) {
        return nextSigner;
      }
    } catch {
      // signer not ready yet
    }

    await sleep(intervalMs);
  }

  throw new Error(
    lastSeenSignerAddress
      ? `Wallet signer still points to ${lastSeenSignerAddress}, expected ${expectedAddress}.`
      : `Wallet signer was not ready for the selected wallet ${expectedAddress}.`,
  );
}

export async function resolveWalletConnectSession({
  openModal = false,
  expectedDifferentFrom = "",
  timeoutMs = 30000,
} = {}) {
  setWalletRestoreSuppressed(false);

  if (openModal) {
    await appKit.open();
  }

  const provider = await waitForWalletProvider(timeoutMs, 250);
  const browserProvider = new ethers.BrowserProvider(provider);

  const address = expectedDifferentFrom
    ? await waitForDifferentAppKitAddress(expectedDifferentFrom, timeoutMs, 250)
    : await waitForAppKitAddress(timeoutMs, 250);

  const signer = await waitForMatchingSigner(
    browserProvider,
    address,
    15000,
    250,
  );

  setCurrentWallet({
    provider: browserProvider,
    signer,
    address,
    walletType: "walletconnect",
  });

  return {
    provider: browserProvider,
    signer,
    address,
  };
}

export async function connectWalletConnect() {
  const result = await resolveWalletConnectSession({
    openModal: true,
  });

  return {
    ...result,
    changed: true,
  };
}

export async function switchWalletConnect() {
  const previousAddress =
    normalizeAddress(getCurrentAddress()) ||
    normalizeAddress(getAppKitAddress());

  const result = await resolveWalletConnectSession({
    openModal: true,
    expectedDifferentFrom: previousAddress,
  });

  return {
    ...result,
    previousAddress,
    changed: normalizeAddress(result.address) !== previousAddress,
  };
}

/**
 * Get current wallet
 */
export function getWallet() {
  if (!currentAddress) {
    try {
      const raw = localStorage.getItem(ACTIVE_WALLET_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        currentAddress = String(parsed?.address || "").trim() || null;
        currentWalletType = parsed?.walletType || null;
      }
    } catch {
      // ignore storage errors
    }
  }

  return {
    provider: currentProvider,
    signer: currentSigner,
    address: currentAddress,
    walletType: currentWalletType,
  };
}

export function getCurrentProvider() {
  return currentProvider;
}

export function getCurrentSigner() {
  return currentSigner;
}

export function getCurrentAddress() {
  if (!currentAddress) {
    try {
      const raw = localStorage.getItem(ACTIVE_WALLET_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        currentAddress = String(parsed?.address || "").trim() || null;
        currentWalletType = parsed?.walletType || null;
      }
    } catch {
      // ignore storage errors
    }
  }

  return currentAddress;
}

export function getCurrentWalletType() {
  return currentWalletType;
}

/**
 * Clear wallet (logout)
 */
export function clearWallet() {
  currentProvider = null;
  currentSigner = null;
  currentAddress = null;
  currentWalletType = null;

  try {
    localStorage.removeItem(ACTIVE_WALLET_KEY);
  } catch {
    // ignore storage errors
  }
}

export function syncWalletFromAppKit() {
  try {
    if (isWalletRestoreSuppressed()) return null;

    const raw = localStorage.getItem(ACTIVE_WALLET_KEY);
    if (raw) return null;

    const appkitData = Object.keys(localStorage)
      .filter((k) => k.startsWith("@appkit"))
      .map((k) => {
        try {
          return JSON.parse(localStorage.getItem(k));
        } catch {
          return null;
        }
      })
      .filter(Boolean);

    for (const item of appkitData) {
      if (item?.accounts?.[0]?.address) {
        const address = item.accounts[0].address;

        setCurrentWallet({
          provider: null,
          signer: null,
          address,
          walletType: "walletconnect",
        });

        return address;
      }
    }
  } catch {
    // ignore
  }

  return null;
}

export function isAppKitConnected() {
  try {
    const explicitConnected = appKit.getIsConnected?.();
    if (typeof explicitConnected === "boolean") {
      return explicitConnected;
    }

    const address = String(appKit.getAddress?.() || "").trim();
    if (address) {
      return true;
    }

    const provider = appKit.getWalletProvider?.();
    if (provider) {
      return true;
    }

    return false;
  } catch {
    return false;
  }
}

export function getAppKitAddress() {
  try {
    return String(appKit.getAddress?.() || "").trim();
  } catch {
    return "";
  }
}

export function getAppKitWalletProvider() {
  try {
    return appKit.getWalletProvider?.() || null;
  } catch {
    return null;
  }
}

function toCaipChainId(chainId) {
  const n = Number(chainId);
  if (!Number.isInteger(n) || n <= 0) {
    throw new Error("Invalid chainId for wallet transaction.");
  }
  return `eip155:${n}`;
}

function toHexChainId(chainId) {
  const n = Number(chainId);
  if (!Number.isInteger(n) || n <= 0) {
    throw new Error("Invalid chainId for wallet switch.");
  }
  return `0x${n.toString(16)}`;
}

function isWalletConnectChainError(err) {
  const message = String(
    err?.message ||
    err?.shortMessage ||
    err?.reason ||
    err?.info?.error?.message ||
    "",
  ).toLowerCase();

  return (
    message.includes("missing or invalid") ||
    message.includes("invalid chainid") ||
    message.includes("invalid chain id") ||
    message.includes("chainid") ||
    message.includes("chain id") ||
    message.includes("unsupported chain") ||
    message.includes("not approved") ||
    message.includes("session")
  );
}

function getWalletConnectChainMessage(chainId) {
  return (
    `This wallet session does not support chain ${chainId}. ` +
    "Disconnect Krionex from your wallet, reconnect, and approve the requested networks. " +
    "For testnet deployment, a desktop browser wallet can be more reliable than some mobile WalletConnect sessions."
  );
}

async function requestWalletConnect(rawProvider, request, chainId) {
  if (!rawProvider?.request) {
    throw new Error("WalletConnect provider is not available.");
  }

  const caipChainId = toCaipChainId(chainId);

  try {
    return await rawProvider.request({
      ...request,
      chainId: caipChainId,
    });
  } catch (firstErr) {
    try {
      return await rawProvider.request(request, caipChainId);
    } catch (secondErr) {
      if (isWalletConnectChainError(secondErr) || isWalletConnectChainError(firstErr)) {
        throw new Error(getWalletConnectChainMessage(chainId));
      }

      throw secondErr || firstErr;
    }
  }
}

export async function switchActiveWalletChain(chainId) {
  const walletType = getCurrentWalletType();
  const rawProvider = getAppKitWalletProvider();
  const appKitActive = Boolean(isAppKitConnected?.() && getAppKitAddress?.());

  if ((walletType === "walletconnect" || appKitActive) && rawProvider?.request) {
    try {
      await requestWalletConnect(
        rawProvider,
        {
          method: "wallet_switchEthereumChain",
          params: [{ chainId: toHexChainId(chainId) }],
        },
        chainId,
      );
    } catch (err) {
      console.warn("[walletClient] walletconnect switch chain failed:", err?.message || err);

      if (isWalletConnectChainError(err)) {
        throw err;
      }

      // Some mobile wallets do not support explicit switch over WalletConnect.
      // If the session already supports the chain, eth_sendTransaction below can still prompt correctly.
    }

    return true;
  }

  if (window.ethereum?.request) {
    try {
      await window.ethereum.request({
        method: "wallet_switchEthereumChain",
        params: [{ chainId: toHexChainId(chainId) }],
      });
      return true;
    } catch (err) {
      if (err?.code === 4902) {
        throw err;
      }

      throw err;
    }
  }

  return false;
}

export async function sendActiveWalletTransaction(
  tx,
  { chainId, expectedAddress } = {},
) {
  if (!tx || !tx.data) {
    throw new Error("Missing tx.data from backend.");
  }

  const expected = normalizeAddress(expectedAddress);
  const walletType = getCurrentWalletType();

  const appKitAddress = normalizeAddress(getAppKitAddress?.() || "");
  const appKitActive = Boolean(isAppKitConnected?.() && appKitAddress);

  // Reown / WalletConnect path.
  // Important: AppKit can still be connected even when currentWalletType is stale.
  if (walletType === "walletconnect" || appKitActive) {
    const wallet = await resolveWalletConnectSession();
    const signerAddress = normalizeAddress(wallet.address || appKitAddress);

    if (expected && signerAddress !== expected) {
      throw new Error("Connected wallet does not match the authenticated wallet.");
    }

    const rawProvider =
      getAppKitWalletProvider() ||
      appKit.getWalletProvider?.() ||
      wallet.provider?.provider ||
      wallet.provider?._provider ||
      wallet.provider ||
      null;

    const request = {
      method: "eth_sendTransaction",
      params: [
        {
          from: signerAddress,
          to: tx.to || undefined,
          data: tx.data,
          value: tx.value || "0x0",
        },
      ],
    };

    if (rawProvider?.request) {
      return requestWalletConnect(rawProvider, request, chainId);
    }

    if (wallet.signer?.sendTransaction) {
      const sent = await wallet.signer.sendTransaction({
        to: tx.to || undefined,
        data: tx.data,
        value: tx.value || "0x0",
      });

      return sent.hash;
    }

    throw new Error("WalletConnect provider is not available for transaction signing.");
  }

  const signer = getCurrentSigner();

  if (signer) {
    const signerAddress = normalizeAddress(await signer.getAddress());

    if (expected && signerAddress !== expected) {
      throw new Error("Connected wallet does not match the authenticated wallet.");
    }

    const sent = await signer.sendTransaction({
      to: tx.to || undefined,
      data: tx.data,
      value: tx.value || "0x0",
    });

    return sent.hash;
  }

  if (window.ethereum?.request) {
    const accounts = await window.ethereum.request({ method: "eth_accounts" });
    const from = normalizeAddress(accounts?.[0]);

    if (expected && from !== expected) {
      throw new Error("Connected wallet does not match the authenticated wallet.");
    }

    if (!from) {
      throw new Error("No active wallet account found. Reconnect your wallet and try again.");
    }

    return window.ethereum.request({
      method: "eth_sendTransaction",
      params: [
        {
          from,
          to: tx.to || undefined,
          data: tx.data,
          value: tx.value || "0x0",
        },
      ],
    });
  }

  throw new Error("No active wallet available for transaction signing.");
}

export function subscribeToAppKitProvider(handler) {
  if (!appKit?.subscribeProvider) {
    return () => { };
  }

  return appKit.subscribeProvider((state) => {
    handler?.(state);
  });
}

export async function disconnectWalletConnect() {
  setWalletRestoreSuppressed(true);

  const previousAddress = String(
    getCurrentAddress() || getAppKitAddress() || "",
  ).trim();

  try {
    await appKit.disconnect?.();
  } catch {
    // ignore if not available
  }

  try {
    await appKit.adapter?.connectionControllerClient?.disconnect?.();
  } catch {
    // ignore fallback disconnect errors
  }

  try {
    await waitForAppKitDisconnect(8000);
  } catch {
    // ignore timeout
  }

  clearWallet();

  if (previousAddress) {
    removeKnownWallet(previousAddress);
  }

  clearWalletConnectorStorageForExplicitDisconnect();
  clearWalletRequestLabel();
  emitWalletChanged();

  return {
    ok: true,
    previousAddress,
  };
}

export async function signWalletMessageForAddress(expectedAddress, message) {
  const expected = normalizeAddress(expectedAddress);

  if (!expected) {
    throw new Error("A valid wallet address is required.");
  }

  if (!message || !String(message).trim()) {
    throw new Error("Message is required for signing.");
  }

  console.log("[walletClient] sign:start", {
    expectedAddress: expected,
    currentAddress: normalizeAddress(getCurrentAddress()),
    walletType: getCurrentWalletType(),
    appKitConnected: isAppKitConnected(),
    appKitAddress: normalizeAddress(getAppKitAddress()),
  });

  const currentWalletType = getCurrentWalletType();
  const appKitAddress = String(getAppKitAddress() || "").trim();
  const appKitConnected = isAppKitConnected();

  // WalletConnect / AppKit path:
  // use raw provider.request(personal_sign) instead of signer.signMessage,
  // because signer.signMessage is what is hanging for you.
  if (
    currentWalletType === "walletconnect" ||
    (appKitConnected && normalizeAddress(appKitAddress) === expected)
  ) {
    console.log("[walletClient] sign:using-appkit-session");

    const wallet = await withTimeout(
      resolveWalletConnectSession(),
      15000,
      "WalletConnect session was not ready. Reconnect the wallet and try again."
    );

    const signerAddress = String(wallet.address || "").trim();

    if (normalizeAddress(signerAddress) !== expected) {
      throw new Error(
        "The connected wallet does not match the wallet address you are trying to verify."
      );
    }

    const rawProvider =
      appKit.getWalletProvider?.() ||
      wallet.provider?.provider ||
      wallet.provider?._provider ||
      null;

    if (!rawProvider?.request) {
      throw new Error("WalletConnect provider is not available for signing.");
    }

    console.log("[walletClient] sign:requesting-signature", {
      signerAddress: normalizeAddress(signerAddress),
      mode: "walletconnect_personal_sign",
    });

    const hexMessage = utf8ToHex(message);

    const signature = await withTimeout(
      rawProvider.request({
        method: "personal_sign",
        params: [hexMessage, signerAddress],
      }),
      60000,
      "Wallet signature timed out. Open your wallet and approve the signature request, then try again."
    );

    console.log("[walletClient] sign:success", {
      signerAddress: normalizeAddress(signerAddress),
      mode: "walletconnect_personal_sign",
    });

    return signature;
  }

  // Injected wallet fallback
  if (!window.ethereum) {
    throw new Error("No wallet provider found for signing.");
  }

  console.log("[walletClient] sign:using-injected-provider");

  const provider = new ethers.BrowserProvider(window.ethereum);
  const signer = await provider.getSigner();
  const signerAddress = String(await signer.getAddress()).trim();

  if (normalizeAddress(signerAddress) !== expected) {
    throw new Error(
      "The connected wallet does not match the wallet address you are trying to verify."
    );
  }

  console.log("[walletClient] sign:requesting-signature", {
    signerAddress: normalizeAddress(signerAddress),
    mode: "injected_signMessage",
  });

  const signature = await withTimeout(
    signer.signMessage(String(message)),
    60000,
    "Wallet signature timed out. Open your wallet and approve the signature request, then try again."
  );

  console.log("[walletClient] sign:success", {
    signerAddress: normalizeAddress(signerAddress),
    mode: "injected_signMessage",
  });

  return signature;
}

export function getActiveWalletRecord() {
  return {
    address: currentAddress,
    walletType: currentWalletType,
  };
}

export function isSameWallet(addressA, addressB) {
  return normalizeAddress(addressA) === normalizeAddress(addressB);
}